import React, {useCallback, useContext, useEffect, useState} from 'react';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import {Button, DialogContentText, Grid} from "@mui/material";
import {useForm} from "react-hook-form";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import {useTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from '@mui/material/Alert';
import {AppContext, AuthContext} from "../../../App";
import {setCloseSnackbar, setOpenSnackbar} from "../../../services/AppServices";
import {changePassword, resetChangePassword} from "../../../services/AuthServices";
import CustomText from "../../widgets/inputs/CustomText";
import {passwordRegister} from "../../../utils/RegisterUtils";
import {getUserNameAndSurname} from "../../../utils/GenericUtils";
import {BaseStyle} from "../../../styles/shared/BaseStyle";
import clsx from "clsx";


export const ChangePasswordDialog = ({open, handleClose}) => {
	const {handleSubmit, errors, control} = useForm({mode: "onChange"});
	const authContext = useContext(AuthContext);
	const appContext = useContext(AppContext);
	const [submit, setSubmit] = useState(false);
	const theme = useTheme();
	const classes = BaseStyle();
	const fullScreen = useMediaQuery(theme.breakpoints.down(400));
	
	const handleCloseSnackbar = useCallback(() => {
		setCloseSnackbar(appContext.dispatch);
	}, [appContext.dispatch]);
	
	const onSubmit = useCallback((data) => {
		setSubmit(true);
		changePassword(data, authContext.dispatch);
	}, [authContext.dispatch, setSubmit]);
	
	const manageChangePassword = useCallback(() => {
		if (!authContext.state.loadingChangePassword && authContext.state.errorChangePassword === "") {
			handleClose();
			setSubmit(false);
			setOpenSnackbar("success", "Password cambiata con successo", appContext.dispatch);
			resetChangePassword(authContext.dispatch);
		} else if (!authContext.state.loadingChangePassword && authContext.state.errorChangePassword.length > 1) {
			setSubmit(false);
			setOpenSnackbar("error", authContext.state.errorChangePassword, appContext.dispatch);
			resetChangePassword(authContext.dispatch);
		}
	}, [appContext.dispatch, authContext.dispatch, authContext.state.errorChangePassword, authContext.state.loadingChangePassword, handleClose]);
	
	useEffect(() => {
		manageChangePassword();
	}, [manageChangePassword]);
	
	return (
		<React.Fragment>
			<Dialog open={open} maxWidth={"sm"} fullWidth={true}
			        fullScreen={fullScreen} PaperProps={{
				className: clsx(classes.roundBorder, classes.shadow24)
			}}>
				<DialogTitle>
					Cambia password
				</DialogTitle>
				<DialogContent>
					<DialogContentText component={"div"}>
						Stai per cambiare la password di {getUserNameAndSurname()}<br/>
						Ricorda che la tua nuova password deve contenere:
						<ul>
							<li>almeno 8 caratteri e non più di 16</li>
							<li>almeno una lettera minuscola ed una lettera maiuscola</li>
							<li>almeno una cifra numerica</li>
							<li>almeno un carattere speciale compreso tra !@#$%^&*</li>
						</ul>
					</DialogContentText>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Grid container spacing={1}>
							<Grid item xs={12}>
								<CustomText
									control={control}
									rules={{required: true}}
									name={"password"}
									label={"Vecchia password"}
									autoFocus={false}
									type={"password"}
									autoComplete={"password"}
									error={errors["password"]}
								/>
							</Grid>
							<Grid item xs={12}>
								<CustomText
									control={control}
									rules={passwordRegister}
									name={"newPassword"}
									label={"Nuova password"}
									autoFocus={false}
									type={"password"}
									autoComplete={"password"}
									error={errors["newPassword"]}
								/>
							</Grid>
							<Grid item xs={12}>
								<CustomText
									control={control}
									rules={passwordRegister}
									name={"newPasswordRepeat"}
									label={"Ripeti nuova password"}
									autoFocus={false}
									type={"password"}
									autoComplete={"password"}
									error={errors["newPasswordRepeat"]}
								/>
							</Grid>
						</Grid>
					</form>
				</DialogContent>
				<DialogActions>
					<Button size={"small"}
					        className={classes.roundButton} onClick={() => handleClose()}>
						Annulla
					</Button>
					<Button type={"submit"} disabled={submit} size="small"
					        disableElevation
					        variant={"contained"}
					        className={clsx(classes.roundButton)} color={"primary"}
					        onClick={handleSubmit(onSubmit)}>
						{
							submit ?
								<CircularProgress size={24}/> : <React.Fragment>Conferma nuova password</React.Fragment>
						}
					</Button>
				</DialogActions>
			</Dialog>
			<Snackbar open={appContext.state.open} autoHideDuration={4000} onClose={handleCloseSnackbar}>
				<Alert style={{borderRadius: 28}} onClose={handleCloseSnackbar} severity={appContext.state.severity}>
					{appContext.state.message}
				</Alert>
			</Snackbar>
		</React.Fragment>
	);
}

function Alert(props) {
	return <MuiAlert elevation={16} variant="filled" {...props} />;
}
